import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Col, Badge } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const ModalDocumentosTributarios = ({ openModal, toggleModal, resultado, file, onSendNotification, notificationSent }) => {
  const [documentosStatus, setDocumentosStatus] = useState({
    success: { 
      texto: 'Documentos cargados exitosamente', 
      color: 'rgba(0, 253, 0, 0.5)', 
      amount: 0 
    },
    error: { 
      texto: 'Documentos con errores', 
      color: 'rgba(253, 0, 0, 0.5)', 
      amount: 0 
    },
    warning: { 
      texto: 'Documentos con advertencias', 
      color: 'rgba(253, 253, 0, 0.5)', 
      amount: 0 
    },
  });

  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationError, setNotificationError] = useState(false);

  const recipientCount = resultado?.notification_recipients_count || 0;
  const canNotify = Boolean(resultado?.success) && recipientCount > 0 && !notificationSent;

  const renderNotificationSection = () => {
    if (!resultado?.success) return null;

    if (recipientCount === 0) {
      return (
        <div className="mt-3 text-center text-muted small">
          No hay destinatarios configurados para notificar sobre esta carga.
        </div>
      );
    }

    return (
      <div
        style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: 'rgba(0, 85, 184, 0.06)',
          borderRadius: '4px',
          textAlign: 'center',
        }}
      >
        {notificationSent ? (
          <p style={{ marginBottom: 0 }}>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Correo enviado a <strong>{recipientCount}</strong>{' '}
            {recipientCount === 1 ? 'destinatario' : 'destinatarios'}.
          </p>
        ) : (
          <p style={{ marginBottom: 0 }}>
            Puede notificar esta carga a <strong>{recipientCount}</strong>{' '}
            {recipientCount === 1 ? 'destinatario' : 'destinatarios'} con el botón
            {' '}<strong>"Enviar correo y cerrar"</strong>. Si cierra sin enviar, no se notificará a nadie.
          </p>
        )}
        {notificationError && (
          <p className="text-danger small" style={{ marginBottom: 0, marginTop: '8px' }}>
            No se pudo enviar el correo. Intente nuevamente.
          </p>
        )}
      </div>
    );
  };

  const handleSendAndClose = async () => {
    setSendingNotification(true);
    setNotificationError(false);
    const sent = await onSendNotification();
    setSendingNotification(false);
    if (sent === false) {
      setNotificationError(true);
      return;
    }
    onCloseModal();
  };

  useEffect(() => {
    if (resultado) {
      setDocumentosStatus(prevStatus => {
        const newStatus = { ...prevStatus };
        
        if (resultado.success) {
          // Success case - show created and updated counts
          newStatus.success.amount = (resultado.created || 0) + (resultado.updated || 0);
          newStatus.error.amount = 0;
          newStatus.warning.amount = resultado.warnings || 0;
        } else {
          // Error case - show error count
          newStatus.success.amount = 0;
          newStatus.error.amount = resultado.errors?.length || resultado.failed || 1;
          newStatus.warning.amount = resultado.warnings || 0;
        }
        
        return newStatus;
      });
    }
  }, [resultado]);

  const onCloseModal = () => {
    setDocumentosStatus({
      success: { ...documentosStatus.success, amount: 0 },
      error: { ...documentosStatus.error, amount: 0 },
      warning: { ...documentosStatus.warning, amount: 0 },
    });
    setNotificationError(false);
    toggleModal();
  };

  // Function to render error details
  const renderErrorDetails = () => {
    if (!resultado?.errors?.length) return null;
    
    return (
      <div style={{ 
        marginTop: '15px',
        maxHeight: '300px',
        overflowY: 'auto',
        border: '1px solid #ddd',
        borderRadius: '4px',
        padding: '10px',
        backgroundColor: '#f8f9fa'
      }}>
        <h6 className="text-danger mb-3">Detalles de errores por línea:</h6>
        <div style={{ fontSize: '13px' }}>
          {resultado.errors.map((error, index) => (
            <div key={index} className="text-danger mb-2" style={{ 
              padding: '5px 0',
              borderBottom: index < resultado.errors.length - 1 ? '1px solid #eee' : 'none'
            }}>
              <strong>•</strong> {error}
            </div>
          ))}
        </div>
        <div className="text-muted small mt-2">
          Total de errores: {resultado.errors.length}
        </div>
      </div>
    );
  };

  // Function to render success details
  const renderSuccessDetails = () => {
    if (!resultado?.success) return null;
    
    return (
      <div style={{ 
        marginTop: '15px',
        padding: '15px',
        backgroundColor: 'rgba(0, 253, 0, 0.1)',
        borderRadius: '4px'
      }}>
        <div className="row">
          <div className="col-md-6">
            <strong>Registros creados:</strong> {resultado.created || 0}
          </div>
          <div className="col-md-6">
            <strong>Registros actualizados:</strong> {resultado.updated || 0}
          </div>
          <div className="col-md-12 mt-2">
            <strong>Total de filas procesadas:</strong> {resultado.total_rows || 0}
          </div>
        </div>
        
        {resultado.nuevos_deudores?.length > 0 && (
          <div className="mt-3">
            <h6>Nuevos deudores creados ({resultado.nuevos_deudores.length}):</h6>
            <ul
              className="list-unstyled"
              style={{
                maxHeight: '200px',
                overflowY: 'auto',
                marginBottom: 0,
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            >
              {resultado.nuevos_deudores.map((deudor, idx) => (
                <li key={idx} className="small">
                  <strong>{deudor.rut}</strong> - {deudor.nombre}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal 
      isOpen={openModal} 
      toggle={onCloseModal} 
      style={{ maxWidth: '800px' }}
      className={resultado?.success ? "modal-success success-container" : "modal-danger danger-container"}
    >
      <ModalHeader style={{ paddingLeft: '6px', color: 'var(--orsan-blue)' }} toggle={onCloseModal}>
        Resultado de Carga de Documentos Tributarios
      </ModalHeader>
      <ModalBody>
        <Col md={{ size: 12 }}>
          <div className="orsan-form-element">
            {resultado?.success ? (
              <p>El proceso de carga de documentos tributarios se ha completado correctamente.</p>
            ) : (
              <div>
                <p>Se han encontrado problemas durante la carga de documentos tributarios.</p>
                {resultado?.errors?.length > 0 && (
                  <p className="text-warning small">
                    <strong>Nota:</strong> Ningún registro fue procesado debido a los errores encontrados. 
                    Corrija el archivo y vuelva a intentarlo.
                  </p>
                )}
              </div>
            )}
          </div>
          <br />

          {/* Status badges */}
          <ul style={{ listStyle: 'none', margin: 'auto', padding: '0', textAlign: 'center' }}>
            {Object.values(documentosStatus)
              .filter(status => status.amount > 0)
              .map((docStatus) => (
                <li
                  key={docStatus.texto}
                  style={{
                    display: 'inline-block',
                    marginRight: '15px',
                    marginBottom: '10px',
                  }}
                >
                  <h5>
                    <Badge style={{ 
                      backgroundColor: docStatus.color,
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}>
                      {docStatus.texto}
                      &nbsp;(
                      <b>{docStatus.amount}</b>
                      )
                    </Badge>
                  </h5>
                </li>
              ))}
          </ul>

          {/* Main message box */}
          <div style={{ 
            marginTop: '20px',
            padding: '15px',
            backgroundColor: resultado?.success ? 'rgba(0, 253, 0, 0.1)' : 'rgba(253, 0, 0, 0.1)',
            borderRadius: '4px',
            borderLeft: `4px solid ${resultado?.success ? 'rgba(0, 253, 0, 0.8)' : 'rgba(253, 0, 0, 0.8)'}`
          }}>
            {resultado?.success ? (
              <div>
                <strong>¡Proceso completado exitosamente!</strong>
                {resultado.message && <p style={{ marginBottom: 0, marginTop: '8px' }}>{resultado.message}</p>}
              </div>
            ) : (
              <div>
                <strong>Error en el proceso:</strong>
                <p style={{ marginBottom: 0, marginTop: '8px' }}>
                  {resultado?.error || 'Ha ocurrido un error durante la carga de documentos.'}
                </p>
              </div>
            )}
          </div>

          {/* Detailed results */}
          {resultado?.success ? renderSuccessDetails() : renderErrorDetails()}
          {renderNotificationSection()}

          {/* Additional details (if any) */}
          {resultado?.details && (
            <div style={{ 
              marginTop: '15px',
              padding: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              <strong>Detalles adicionales:</strong>
              <pre style={{ marginBottom: 0, marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                {resultado.details}
              </pre>
            </div>
          )}
        </Col>
      </ModalBody>
      <ModalFooter style={{ justifyContent: 'center' }}>
        {canNotify ? (
          <>
            <Button
              className="btn-orsan-secondary"
              onClick={onCloseModal}
              disabled={sendingNotification}
              style={{ minWidth: 165 }}
            >
              Cerrar sin enviar
            </Button>
            <Button
              className="btn-orsan-primary"
              onClick={handleSendAndClose}
              disabled={sendingNotification}
              style={{ minWidth: 165 }}
            >
              {sendingNotification ? 'Enviando...' : 'Enviar correo y cerrar'}
            </Button>
          </>
        ) : (
          <Button
            className={resultado?.success ? "btn-orsan-primary" : "btn-orsan-secondary"}
            onClick={onCloseModal}
            style={{ width: 165 }}
          >
            Aceptar
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

ModalDocumentosTributarios.defaultProps = {
  resultado: {},
  file: null,
  onSendNotification: () => {},
  notificationSent: false,
};

export default ModalDocumentosTributarios;